import type {
    PriceListUerRowCreateInput,
    PriceListUerRowPriceCurrentCreateInput,
    PriceListUerRowUpdateInput,
    PriceListUerRowWorkInput,
    UerCurrentEstimateBasisQuery,
} from 'gql/graphql';
import { PriceConstructionTypeRelationStatusEnum, UerRecordStatusEnum, WorkInSumEnum } from 'gql/graphql';
import type { AnyObject } from 'types/AnyObject';
import { replacerCost } from 'utils/replacerCost';

import * as R from 'ramda';

interface IPrepareAddUerData {
    formData: Record<string, any>;
    checkboxValues: Record<string, boolean>;
}

export const prepareAddUerData = (props: IPrepareAddUerData): PriceListUerRowCreateInput => {
    const { formData, checkboxValues } = props;

    const currentPrices = Object.entries(checkboxValues).reduce(
        (acc: Record<string, number | null>[], [key, value]) => {
            if (value) {
                const { unitCostWithOther } = formData[key];

                acc = [...acc, { ...formData[key], unitCostWithOther: replacerCost(unitCostWithOther) }];
            }

            return acc;
        },
        [],
    );

    const groupsDemitemsGroupIds = (formData.groupsDismantling ?? [])
        .filter((item: { dataGroup: boolean }) => item.dataGroup)
        .map((item: { currentId: number }) => item.currentId);
    const groupsDemitemsItemIds = (formData.groupsDismantling ?? [])
        .filter((item: { dataGroup: boolean }) => !item.dataGroup)
        .map((item: { subGroupId: number }) => item.subGroupId);

    const constructionTypeFieldKeys = Object.keys(formData).filter((key) => {
        const value = formData[key];

        return value != null && typeof value === 'object' && 'unitCostWithOther' in value;
    });

    const variablesData = {
        ...R.omit(
            [
                ...constructionTypeFieldKeys,
                'uerClassifier',
                'ratioItems',
                'ratioGroups',
                'groupsDismantling',
                'dismantlingCoefIds',
            ],
            formData,
        ),
        ratioGroupIds: [...(formData.ratioGroupIds ?? []), ...groupsDemitemsGroupIds, ...groupsDemitemsItemIds],
        ratioIds: [...(formData.ratioIds ?? [])],
    };

    const workKind: PriceListUerRowWorkInput = {
        workInSum: formData?.workInSum?.id ?? WorkInSumEnum.NotParticipate,
        workUniqueId: formData?.workKind?.id,
        workName: formData?.workKind?.label,
        workRatio: replacerCost(formData?.workRatio),
        workUnit: formData?.workUnit,
        workCode: formData?.workCode,
    };

    return {
        ...variablesData,
        currentPrices,
        ...(formData.chapter && { chapter: formData?.chapter?.id }),
        ...(formData.uerClassifier && { uerClassifier: formData?.uerClassifier?.id }),
        workKind,
    };
};

export const prepareUpdateUerData = (
    uerId: string,
    submitData: Record<string, any>,
    uerRecordStatus: UerRecordStatusEnum | undefined,
    checkboxValues: Record<string, boolean>,
    estimateUploaded: boolean,
    currentEstimateBasisData?: UerCurrentEstimateBasisQuery['uerCurrentEstimateBasis'],
): PriceListUerRowUpdateInput => {
    const isUerRecordStatusAdditionalCondition = uerRecordStatus === UerRecordStatusEnum.AdditionalCondition;

    const currentPrices: PriceListUerRowPriceCurrentCreateInput[] = Object.entries(checkboxValues).reduce(
        (acc: PriceListUerRowPriceCurrentCreateInput[], [key, value]) => {
            const isNotStatusConditions =
                submitData[key]?.constructionTypeRelationStatus !==
                    PriceConstructionTypeRelationStatusEnum.ContractCondition &&
                submitData[key]?.constructionTypeRelationStatus !==
                    PriceConstructionTypeRelationStatusEnum.AdditionalCondition;

            if (value && isNotStatusConditions) {
                if (currentEstimateBasisData?.data?.currentPrices) {
                    const objCurrentEstimate = currentEstimateBasisData?.data?.currentPrices?.reduce(
                        (accI: any, valueI: any) => {
                            if (Number(key) === valueI?.constructionTypeId) {
                                accI = { ...accI, [valueI.constructionTypeId]: valueI };
                            }

                            return accI;
                        },
                        {},
                    );

                    acc.push({ ...objCurrentEstimate[key], constructionTypeId: Number(key) });
                } else {
                    const unitCostWithOther = submitData?.currentPrices?.find(
                        (price: AnyObject) => price.constructionTypeId === submitData[key].constructionTypeId,
                    )?.unitCostWithOther;

                    if (unitCostWithOther) {
                        acc.push(
                            R.omit(['id', 'constructionTypeName', 'constructionTypeRelationStatus'], {
                                ...submitData[key],
                                unitCost: unitCostWithOther,
                                unitCostWithOther,
                            }) as PriceListUerRowPriceCurrentCreateInput,
                        );
                    }
                }
            }

            return acc;
        },
        [],
    );

    const resultObject: PriceListUerRowUpdateInput = {
        id: Number(uerId),
        ...(submitData.chapter && { chapter: submitData?.chapter?.id }),
        ...(submitData.uerClassifier && { uerClassifier: submitData?.uerClassifier?.id }),
        estimateUploaded,
        version: submitData?.version,
    };

    const ratioGroupIds = submitData.ratioGroups?.length
        ? submitData.ratioGroups.map((group: { groupId: number }) => group.groupId)
        : [];
    const ratioIds = submitData.ratioItems?.length
        ? submitData.ratioItems.map((group: { rationId: number }) => group.rationId)
        : [];

    const workKind: PriceListUerRowWorkInput = {
        workInSum: submitData?.workInSum?.id,
        workUniqueId: submitData?.workKind?.id,
        workName: submitData?.workKind?.label,
        workRatio: replacerCost(submitData?.workRatio),
        workUnit: submitData?.workUnit,
        workCode: submitData?.workCode,
    };

    const updateCurrentPrices: PriceListUerRowUpdateInput = {
        ...resultObject,
        justification: submitData?.justification || null,
        basePrice: submitData?.basePrice || null,
        ...(currentPrices.length ? { currentPrices } : { currentPrices: [] }),
        ratioGroupIds,
        ratioIds,
        workKind,
    };

    if (currentPrices.length || isUerRecordStatusAdditionalCondition) {
        return estimateUploaded ? { ...updateCurrentPrices, name: submitData?.name || null } : updateCurrentPrices;
    }

    return { ...resultObject, workKind };
};
